import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CustomerBrowseItem, CustomerActivationResult } from '../types/customer';

/**
 * Customer API utilities for interacting with the customer management backend
 */

/**
 * Get all customers assigned to the current user (Registry + Overrides)
 * @returns Promise<CustomerBrowseItem[]> List of customers available to the user
 */
export const browseCustomers = async (): Promise<CustomerBrowseItem[]> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const response = await get({
      apiName: 'api',
      path: '/api/customers/browse',
      options: {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    }).response;

    const data = (await response.body.json()) as any;
    return data.customers || [];
  } catch (error) {
    console.error('Error browsing customers:', error);
    throw new Error('Failed to fetch customer list');
  }
};

/**
 * Activate a customer for document management
 * @param customerId The customer ID to activate
 * @returns Promise<CustomerActivationResult> Activation result with customer and engagement details
 */
export const activateCustomer = async (customerId: string): Promise<CustomerActivationResult> => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const response = await post({
      apiName: 'api',
      path: `/api/customers/${customerId}/activate`,
      options: {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    }).response;

    const data = (await response.body.json()) as any;
    return data;
  } catch (error) {
    console.error('Error activating customer:', error);
    throw new Error('Failed to activate customer');
  }
};

/**
 * Check if a customer is activated (has document repository)
 * @param customerId The customer ID to check
 * @returns Promise<boolean> True if customer is activated
 */
export const isCustomerActivated = async (customerId: string): Promise<boolean> => {
  try {
    const customers = await browseCustomers();
    const customer = customers.find((c) => c.customerId === customerId);
    return customer?.status === 'ACTIVATED';
  } catch (error) {
    console.error('Error checking customer activation status:', error);
    return false;
  }
};

/**
 * Get customer activation status and document count
 * @param customerId The customer ID
 * @returns Promise<{activated: boolean, documentCount: number}> Activation status and document count
 */
export const getCustomerStatus = async (customerId: string): Promise<{ activated: boolean; documentCount: number }> => {
  try {
    const customers = await browseCustomers();
    const customer = customers.find((c) => c.customerId === customerId);

    if (!customer) {
      throw new Error('Customer not found or not accessible');
    }

    return {
      activated: customer.status === 'ACTIVATED',
      documentCount: customer.documentCount,
    };
  } catch (error) {
    console.error('Error getting customer status:', error);
    throw error;
  }
};
